import Immutable from 'immutable';
import { StudioFolderImportRequest } from 'records/StudioFolderImportRequest';
import { StudioImportRequest } from 'records/StudioImportRequest';
import {
  RawStudioProject,
  StudioProject,
  StudioProjectList,
} from 'records/StudioProject';
import {
  RawStudioProjectFolder,
  StudioProjectFolder,
} from 'records/StudioProjectFolder';
import GQLRequest from 'utils/GQLRequest';

export interface ExportAllRequest {
  projectid: string;
  drawings: boolean;
  punch: boolean;
  rfis: boolean;
  submittals: boolean;
  userlog: boolean;
}

export const getStudioProjects = async (): Promise<StudioProjectList> => {
  const { getStudioProjects: studioProjects } = await new GQLRequest()
    .query(
      `
      query GetStudioProjects() {
        getStudioProjects() {
            id
            guid
            name
            restricted
            created
            ownerNameIdentifier
            ownerEmail
        }
      }
      `
    )
    .variables({})
    .json<{ getStudioProjects: RawStudioProject[] }>();

  return Immutable.List(studioProjects.map(StudioProject.deserialize));
};

export const getStudioProjectFolder = async (
  projectID: string,
  studioID: number
): Promise<StudioProjectFolder> => {
  const { getStudioProjectFolder: studioProjectFolder } = await new GQLRequest()
    .query(
      `
        query GetStudioProjectFolder($studioprojectid: String!, $id: Int) {
            getStudioProjectFolder(studioprojectid: $studioprojectid, id: $id) {
                id
                name
                path
                parentfolderid
                created
                permission
                studioprojectid
                folders {
                    id
                    name
                    path
                    parentfolderid
                    created
                    permission
                    studioprojectid
                }
                files {
                    id
                    name
                    path
                    parentfolderid
                    permission
                    created
                    size
                    isLocked
                    inSession
                    revisionID
                    studioprojectid
                }
          }
        }
        `
    )
    .variables({ studioprojectid: projectID, id: studioID })
    .json<{ getStudioProjectFolder: RawStudioProjectFolder }>();

  return StudioProjectFolder.deserialize(studioProjectFolder);
};

export const importStudioFiles = async (
  projectID: string,
  requests: StudioImportRequest[],
  folders: StudioFolderImportRequest[]
): Promise<string> => {
  const reqs = Immutable.Set<StudioImportRequest>(requests).toJS();

  const { importStudioFiles: importFromStudio } = await new GQLRequest()
    .query(
      `
      mutation ImportStudioFiles($projectid: String!, $requests: [StudioImportRequest], $folders: [StudioFolderImportRequest!]) {
        importStudioFiles(projectid: $projectid, requests: $requests, folders: $folders)
    }
      `
    )
    .variables({
      projectid: projectID,
      requests: reqs,
      folders,
    })
    .json<{ importStudioFiles: string }>();
  return importFromStudio;
};

export const exportAllDataToStudioFolder = async (
  parentfolderid: number,
  studioprojectid: string,
  projectid: string,
  drawings: boolean,
  punch: boolean,
  rfis: boolean,
  submittals: boolean,
  users: boolean
): Promise<string> => {
  const input: ExportAllRequest = {
    projectid,
    drawings,
    punch,
    rfis,
    submittals,
    userlog: users,
  };

  const { exportAllDataToStudio: exportToStudio } = await new GQLRequest()
    .query(
      `
      query ExportAllDataToStudio($input: ExportAllRequest!, $studioprojectid: String!, $parentfolderid: Int!) {
        exportAllDataToStudio(input: $input, studioprojectid: $studioprojectid, parentfolderid: $parentfolderid) {
          extension
          url
        }
    }
      `
    )
    .variables({
      input,
      studioprojectid,
      parentfolderid,
    })
    .json<{ exportAllDataToStudio: string }>();

  return exportToStudio;
};

export const createNewStudioFolder = async (
  folderName: string,
  projectId: string,
  studioProjectId: string,
  parentFolderId: string
): Promise<number> => {
  const { createStudioFolder } = await new GQLRequest()
    .query(
      `
      mutation($projectid: String!,$studioprojectid: String!, $parentfolderid: Int!,$name: String! ){
        createStudioFolder(projectid: $projectid,  studioprojectid: $studioprojectid, parentfolderid:$parentfolderid, name: $name)
      }
      `
    )
    .variables({
      studioprojectid: studioProjectId,
      projectid: projectId,
      name: folderName,
      parentfolderid: +parentFolderId,
    })
    .json<{ createStudioFolder: number }>();
  return createStudioFolder;
};
